import type { Audit } from '@compass/client/types/audit';
import type { CallInfo } from '@compass/client/types/call';
import type { EligibilityCheck } from '@compass/client/types/eligibility';
import type { Email } from '@compass/client/types/email';
import type { GenericTask } from '@compass/client/types/generic_task';
import type { Text } from '@compass/client/types/text';

/**
 * Sentinel id for the pending-attempt pill. It is UI-only — no call object exists behind
 * it — so every id-keyed fetch on the page must skip it (they all already gate on the
 * selected item's `type`, which is never `'call'` for this one).
 */
export const PENDING_HUMAN_CALL_TAB_ID = 'pending-human-call';

/** `data` for the pending-attempt pill: there is no server object to carry. */
export interface PendingHumanCall {
  id: typeof PENDING_HUMAN_CALL_TAB_ID;
}

export type TabItemType =
  | 'call'
  /** The human call the case is waiting on — a placeholder until one is dialled. */
  | 'pending_human_call'
  | 'eligibility'
  | 'generic_task'
  | 'text'
  | 'email'
  | 'ai_audit'
  | 'human_audit';

// Base tab item interface - backward compatible with old usage
export interface TabItem {
  type: TabItemType;
  id: string;
  tabNumber: number;
  data: CallInfo | EligibilityCheck | GenericTask | Text | Email | Audit | PendingHumanCall;
}

export interface CallsTabsProps {
  tabItems: TabItem[];
  selectedTabId: string | null;
  onTabSelect: (tabId: string) => void;
  isHumanAudit: boolean;
  onCancelCall?: (callId: string) => void;
  onRetryTask?: (taskId: string) => void;
  workflowType?: string;
  onUpdateChartNotes?: (callId: string, notes: string) => Promise<void>;
  onSelectCall?: (callId: string) => void;
  belowContent?: React.ReactNode;
  /** Node pinned to the right edge of the tab strip (e.g. AI Audit sub-pills). */
  trailingContent?: React.ReactNode;
}
